import React, { useState, useRef } from "react";
import { Mantra } from "../types";
import {
  ChevronDown,
  ChevronUp,
  BookOpen,
  Volume2,
  Sparkles,
  Layers,
  Activity,
  Info,
  Globe,
  FileText,
  CheckCircle2,
  ListFilter,
  VolumeX,
  Repeat
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { createAudioContext } from "../lib/audio-utils";

interface MantraMetadataAccordionProps {
  mantra: Mantra;
  allMantras?: Mantra[];
  onSelectMantra?: (mantra: Mantra) => void;
}

export default function MantraMetadataAccordion({
  mantra,
  allMantras = [],
  onSelectMantra,
}: MantraMetadataAccordionProps) {
  // Accordion section collapse/expand state
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    phonetics: true,
    grammar: false,
    history: false,
    pitch: false,
    pronunciation: false,
  });

  // Library mode state: "active" vs "library"
  const [viewMode, setViewMode] = useState<"active" | "library">("active");
  const [expandedMantraId, setExpandedMantraId] = useState<string | null>(mantra.id);
  const [activeLoopSyllableIdx, setActiveLoopSyllableIdx] = useState<number | null>(null);

  const loopIntervalRef = useRef<any>(null);

  const baseFreqs = {
    Udatta: 220,
    Svarita: 165,
    Anudatta: 110,
  };

  const toggleSyllableLoop = (idx: number, expectedPitch: string) => {
    if (activeLoopSyllableIdx === idx) {
      stopSyllableLoop();
      return;
    }

    stopSyllableLoop();
    setActiveLoopSyllableIdx(idx);

    const freq = baseFreqs[expectedPitch as keyof typeof baseFreqs] || 165;
    playSyllablePitchPulse(freq);

    loopIntervalRef.current = setInterval(() => {
      playSyllablePitchPulse(freq);
    }, 1800);
  };

  const playSyllablePitchPulse = (freqHz: number) => {
    try {
      const ctx = createAudioContext();
      if (!ctx) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sine";
      osc.frequency.setValueAtTime(freqHz, ctx.currentTime);

      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.12, ctx.currentTime + 0.08);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.2);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start();
      osc.stop(ctx.currentTime + 1.2);
    } catch (e) {
      console.warn("Loop pulse failed:", e);
    }
  };

  const stopSyllableLoop = () => {
    if (loopIntervalRef.current) {
      clearInterval(loopIntervalRef.current);
      loopIntervalRef.current = null;
    }
    setActiveLoopSyllableIdx(null);
  };

  const phoneticSegments = mantra?.phoneticSegments || [];
  const pitchZones = mantra?.pitchZones || [];
  const pronunciationTips = mantra?.pronunciationTips || [];

  const toggleSection = (sectionKey: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const expandAll = () => {
    setOpenSections({
      phonetics: true,
      grammar: true,
      history: true,
      pitch: true,
      pronunciation: true,
    });
  };

  const collapseAll = () => {
    setOpenSections({
      phonetics: false,
      grammar: false,
      history: false,
      pitch: false,
      pronunciation: false,
    });
  };

  // Helper for pitch badge styling
  const getPitchBadgeColor = (pitch: string) => {
    switch (pitch) {
      case "Udatta":
      case "High":
        return "bg-amber-500/15 text-amber-300 border-amber-500/30";
      case "Anudatta":
      case "Low":
        return "bg-indigo-500/15 text-indigo-300 border-indigo-500/30";
      case "Svarita":
      case "Middle":
        return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
      default:
        return "bg-neutral-800 text-neutral-300 border-neutral-700";
    }
  };

  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-5 md:p-6 shadow-xl text-neutral-200">
      {/* Header bar with controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-neutral-800/80 pb-4 mb-5">
        <div className="flex items-center gap-2.5">
          <div className="bg-amber-500/10 text-amber-400 p-2 rounded-xl border border-amber-500/20">
            <BookOpen className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-neutral-100 uppercase tracking-wider font-mono flex items-center gap-2">
              <span>Mantra Context & Metadata Accordion</span>
            </h3>
            <p className="text-[11px] text-neutral-400">
              Interactive phonetic, grammatical, and historical analysis
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-auto">
          {allMantras.length > 0 && (
            <div className="flex bg-neutral-950 p-1 rounded-xl border border-neutral-800 text-[11px] font-mono font-semibold">
              <button
                onClick={() => setViewMode("active")}
                className={`px-3 py-1 rounded-lg transition cursor-pointer ${
                  viewMode === "active"
                    ? "bg-amber-500 text-neutral-950 font-bold shadow-sm"
                    : "text-neutral-400 hover:text-neutral-200"
                }`}
              >
                Focused Mantra
              </button>
              <button
                onClick={() => setViewMode("library")}
                className={`px-3 py-1 rounded-lg transition cursor-pointer ${
                  viewMode === "library"
                    ? "bg-amber-500 text-neutral-950 font-bold shadow-sm"
                    : "text-neutral-400 hover:text-neutral-200"
                }`}
              >
                Library ({allMantras.length})
              </button>
            </div>
          )}

          {viewMode === "active" && (
            <div className="flex items-center gap-1.5 text-[10px] font-mono">
              <button
                onClick={expandAll}
                className="px-2.5 py-1 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 rounded-lg border border-neutral-700 transition cursor-pointer"
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="px-2.5 py-1 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 rounded-lg border border-neutral-700 transition cursor-pointer"
              >
                Collapse All
              </button>
            </div>
          )}
        </div>
      </div>

      {/* VIEW MODE 1: FOCUSED ACTIVE MANTRA ACCORDION */}
      {viewMode === "active" && (
        <div className="space-y-3">
          {/* Active Mantra Badge Card */}
          <div className="bg-neutral-950 border border-neutral-800 rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-[10px] font-mono font-bold text-amber-400 uppercase">
                  {mantra.tongue}
                </span>
                <span className="px-2 py-0.5 rounded bg-neutral-800 text-[10px] font-mono text-neutral-400 uppercase">
                  {mantra.tradition} Tradition
                </span>
              </div>
              <h4 className="text-base font-bold text-amber-200">{mantra.title}</h4>
            </div>

            <div className="text-right">
              <p className="text-sm font-bold text-neutral-300 font-serif leading-tight">
                {mantra.sanskrit}
              </p>
              <p className="text-[11px] italic text-neutral-400 font-mono mt-0.5">
                {mantra.iast}
              </p>
            </div>
          </div>

          {/* ACCORDION ITEM 1: PHONETIC & SVARA ACCENT BREAKDOWN */}
          <div className="border border-neutral-800/90 rounded-xl overflow-hidden bg-neutral-950/60">
            <button
              onClick={() => toggleSection("phonetics")}
              className="w-full px-4 py-3.5 flex items-center justify-between bg-neutral-900/80 hover:bg-neutral-800/60 transition cursor-pointer text-left"
            >
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20">
                  <Activity className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-bold text-neutral-200 uppercase tracking-wider font-mono">
                    1. Phonetic Syllables & Svara Accent Map
                  </span>
                  <span className="ml-2 text-[10px] text-neutral-400 font-mono">
                    ({phoneticSegments.length} Syllables Analyzed)
                  </span>
                </div>
              </div>
              <div className="text-neutral-400">
                {openSections.phonetics ? (
                  <ChevronUp className="w-4 h-4 text-amber-400" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </div>
            </button>

            <AnimatePresence>
              {openSections.phonetics && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 border-t border-neutral-800/80 space-y-3">
                    <p className="text-[11px] text-neutral-400 leading-relaxed">
                      Detailed vocal tract breakdown for every syllable. Pitch registers (Udatta / Svarita / Anudatta) specify exact vocal resonance directions.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2.5">
                      {phoneticSegments.map((seg, idx) => {
                        const isLooping = activeLoopSyllableIdx === idx;
                        return (
                          <div
                            key={idx}
                            className={`border rounded-xl p-3 flex flex-col justify-between transition ${
                              isLooping
                                ? "bg-amber-950/40 border-amber-500/50 shadow-lg shadow-amber-500/10"
                                : "bg-neutral-900/90 border-neutral-800"
                            }`}
                          >
                            <div>
                              <div className="flex items-center justify-between gap-1 mb-1.5">
                                <span className="text-sm font-bold text-amber-300 font-serif">
                                  {seg.syllable}
                                </span>
                                <span
                                  className={`px-1.5 py-0.5 rounded text-[9px] font-mono font-bold border ${getPitchBadgeColor(
                                    seg.expectedPitch
                                  )}`}
                                >
                                  {seg.expectedPitch}
                                </span>
                              </div>

                              <div className="space-y-1 mb-2">
                                <span className="text-[9px] font-mono uppercase text-neutral-400 bg-neutral-950 px-1.5 py-0.5 rounded border border-neutral-800 inline-block">
                                  {seg.phoneticType}
                                </span>
                                <p className="text-[10px] text-neutral-400 leading-tight">
                                  {seg.description}
                                </p>
                              </div>
                            </div>

                            <button
                              type="button"
                              onClick={() => toggleSyllableLoop(idx, seg.expectedPitch)}
                              className={`w-full mt-1 px-2 py-1 rounded-lg text-[9px] font-mono font-bold flex items-center justify-center gap-1 transition cursor-pointer border ${
                                isLooping
                                  ? "bg-amber-500 text-neutral-950 border-amber-400 animate-pulse"
                                  : "bg-neutral-950 text-amber-400 border-amber-500/30 hover:bg-amber-500/10"
                              }`}
                            >
                              {isLooping ? <VolumeX className="w-3 h-3" /> : <Repeat className="w-3 h-3" />}
                              <span>{isLooping ? "Stop Syllable Loop" : "Practice Syllable Loop"}</span>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ACCORDION ITEM 2: GRAMMATICAL & ETYMOLOGICAL CONTEXT */}
          <div className="border border-neutral-800/90 rounded-xl overflow-hidden bg-neutral-950/60">
            <button
              onClick={() => toggleSection("grammar")}
              className="w-full px-4 py-3.5 flex items-center justify-between bg-neutral-900/80 hover:bg-neutral-800/60 transition cursor-pointer text-left"
            >
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                  <FileText className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-bold text-neutral-200 uppercase tracking-wider font-mono">
                    2. Grammatical, Metrical & Word Structure
                  </span>
                </div>
              </div>
              <div className="text-neutral-400">
                {openSections.grammar ? (
                  <ChevronUp className="w-4 h-4 text-indigo-400" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </div>
            </button>

            <AnimatePresence>
              {openSections.grammar && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 border-t border-neutral-800/80 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="bg-neutral-900/80 border border-neutral-800 p-3.5 rounded-xl">
                        <span className="text-[10px] font-mono font-bold text-indigo-400 uppercase tracking-wider block mb-1">
                          Exact Literal Translation
                        </span>
                        <p className="text-xs text-neutral-300 leading-relaxed italic">
                          "{mantra.translation}"
                        </p>
                      </div>

                      <div className="bg-neutral-900/80 border border-neutral-800 p-3.5 rounded-xl space-y-1.5">
                        <span className="text-[10px] font-mono font-bold text-indigo-400 uppercase tracking-wider block">
                          Linguistic Identity
                        </span>
                        <div className="flex flex-wrap gap-1.5 text-[10px] font-mono">
                          <span className="bg-neutral-950 border border-neutral-800 text-neutral-300 px-2 py-0.5 rounded">
                            Tongue: {mantra.tongue}
                          </span>
                          <span className="bg-neutral-950 border border-neutral-800 text-neutral-300 px-2 py-0.5 rounded">
                            Transliteration: IAST
                          </span>
                          <span className="bg-neutral-950 border border-neutral-800 text-neutral-300 px-2 py-0.5 rounded">
                            ID: {mantra.id}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ACCORDION ITEM 3: HISTORICAL & THEOLOGICAL CONTEXT */}
          <div className="border border-neutral-800/90 rounded-xl overflow-hidden bg-neutral-950/60">
            <button
              onClick={() => toggleSection("history")}
              className="w-full px-4 py-3.5 flex items-center justify-between bg-neutral-900/80 hover:bg-neutral-800/60 transition cursor-pointer text-left"
            >
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  <Globe className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-bold text-neutral-200 uppercase tracking-wider font-mono">
                    3. Historical & Sacred Theological Significance
                  </span>
                </div>
              </div>
              <div className="text-neutral-400">
                {openSections.history ? (
                  <ChevronUp className="w-4 h-4 text-emerald-400" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </div>
            </button>

            <AnimatePresence>
              {openSections.history && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 border-t border-neutral-800/80 space-y-3">
                    <div className="bg-neutral-900/80 border border-neutral-800 p-3.5 rounded-xl">
                      <span className="text-[10px] font-mono font-bold text-emerald-400 uppercase tracking-wider block mb-1">
                        Ritual & Spiritual Origin
                      </span>
                      <p className="text-xs text-neutral-300 leading-relaxed">
                        {mantra.significance}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ACCORDION ITEM 4: ACOUSTIC PITCH ZONES & RESONANCE */}
          <div className="border border-neutral-800/90 rounded-xl overflow-hidden bg-neutral-950/60">
            <button
              onClick={() => toggleSection("pitch")}
              className="w-full px-4 py-3.5 flex items-center justify-between bg-neutral-900/80 hover:bg-neutral-800/60 transition cursor-pointer text-left"
            >
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-orange-500/10 text-orange-400 border border-orange-500/20">
                  <Volume2 className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-bold text-neutral-200 uppercase tracking-wider font-mono">
                    4. Frequency Targets & Pitch Registers (Hz)
                  </span>
                  <span className="ml-2 text-[10px] text-neutral-400 font-mono">
                    ({pitchZones.length} Zones Defined)
                  </span>
                </div>
              </div>
              <div className="text-neutral-400">
                {openSections.pitch ? (
                  <ChevronUp className="w-4 h-4 text-orange-400" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </div>
            </button>

            <AnimatePresence>
              {openSections.pitch && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 border-t border-neutral-800/80 space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      {pitchZones.map((zone, idx) => (
                        <div
                          key={idx}
                          className="bg-neutral-900/90 border border-neutral-800 rounded-xl p-3 space-y-1.5"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-orange-300 font-mono">
                              {zone.label} Register
                            </span>
                            <span className="text-[10px] font-mono bg-neutral-950 px-2 py-0.5 rounded text-orange-400 border border-neutral-800">
                              {zone.minHz} - {zone.maxHz} Hz
                            </span>
                          </div>
                          <p className="text-[10px] text-neutral-400 leading-tight">
                            {zone.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ACCORDION ITEM 5: PRONUNCIATION & ORAL TRADITION GUIDELINES */}
          <div className="border border-neutral-800/90 rounded-xl overflow-hidden bg-neutral-950/60">
            <button
              onClick={() => toggleSection("pronunciation")}
              className="w-full px-4 py-3.5 flex items-center justify-between bg-neutral-900/80 hover:bg-neutral-800/60 transition cursor-pointer text-left"
            >
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-bold text-neutral-200 uppercase tracking-wider font-mono">
                    5. Oral Tradition & Articulatory Guidelines
                  </span>
                  <span className="ml-2 text-[10px] text-neutral-400 font-mono">
                    ({pronunciationTips.length} Expert Rules)
                  </span>
                </div>
              </div>
              <div className="text-neutral-400">
                {openSections.pronunciation ? (
                  <ChevronUp className="w-4 h-4 text-cyan-400" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </div>
            </button>

            <AnimatePresence>
              {openSections.pronunciation && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 border-t border-neutral-800/80 space-y-2">
                    {pronunciationTips.map((tip, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2.5 bg-neutral-900/80 border border-neutral-800 p-2.5 rounded-xl"
                      >
                        <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-neutral-300 leading-relaxed">{tip}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* VIEW MODE 2: ALL MANTRAS LIBRARY ACCORDION GRID */}
      {viewMode === "library" && (
        <div className="space-y-3">
          <p className="text-xs text-neutral-400 mb-2">
            Click any mantra card below to expand its full phonetic and historical metadata accordion or select it for active chanting practice.
          </p>

          <div className="space-y-3">
            {allMantras.map((item) => {
              const isExpanded = expandedMantraId === item.id;
              const isSelected = mantra.id === item.id;

              return (
                <div
                  key={item.id}
                  className={`border rounded-xl transition-all overflow-hidden ${
                    isSelected
                      ? "border-amber-500/50 bg-amber-500/5 shadow-lg shadow-amber-500/5"
                      : "border-neutral-800 bg-neutral-950 hover:border-neutral-700"
                  }`}
                >
                  <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded bg-neutral-800 border border-neutral-700 text-[9px] font-mono font-bold text-amber-300 uppercase">
                          {item.tongue}
                        </span>
                        {isSelected && (
                          <span className="px-2 py-0.5 rounded bg-amber-500 text-neutral-950 text-[9px] font-mono font-bold uppercase">
                            Currently Active
                          </span>
                        )}
                      </div>
                      <h4 className="text-sm font-bold text-neutral-100">{item.title}</h4>
                      <p className="text-xs font-serif text-amber-200">{item.sanskrit}</p>
                    </div>

                    <div className="flex items-center gap-2 self-start md:self-auto">
                      {onSelectMantra && !isSelected && (
                        <button
                          onClick={() => onSelectMantra(item)}
                          className="px-3 py-1.5 bg-amber-500 text-neutral-950 font-bold rounded-lg text-xs hover:bg-amber-400 transition cursor-pointer"
                        >
                          Select for Chanting
                        </button>
                      )}

                      <button
                        onClick={() =>
                          setExpandedMantraId(isExpanded ? null : item.id)
                        }
                        className="px-3 py-1.5 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 rounded-lg text-xs font-mono font-semibold transition cursor-pointer flex items-center gap-1"
                      >
                        <span>{isExpanded ? "Hide Details" : "Inspect Metadata"}</span>
                        {isExpanded ? (
                          <ChevronUp className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronDown className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* EXPANDED METADATA FOR LIBRARY ITEM */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-neutral-800/80 p-4 bg-neutral-900/60 space-y-3"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                          <div className="bg-neutral-950 p-3 rounded-lg border border-neutral-800">
                            <span className="text-[10px] font-mono font-bold text-neutral-400 block uppercase mb-1">
                              Translation
                            </span>
                            <p className="text-neutral-300">{item.translation}</p>
                          </div>
                          <div className="bg-neutral-950 p-3 rounded-lg border border-neutral-800">
                            <span className="text-[10px] font-mono font-bold text-neutral-400 block uppercase mb-1">
                              Significance
                            </span>
                            <p className="text-neutral-300">{item.significance}</p>
                          </div>
                        </div>

                        <div>
                          <span className="text-[10px] font-mono font-bold text-neutral-400 block uppercase mb-1.5">
                            Syllables & Accents ({(item.phoneticSegments || []).length})
                          </span>
                          <div className="flex flex-wrap gap-1.5">
                            {(item.phoneticSegments || []).map((seg, sIdx) => (
                              <span
                                key={sIdx}
                                className="px-2 py-1 rounded bg-neutral-950 border border-neutral-800 text-[10px] font-mono text-neutral-200 flex items-center gap-1"
                              >
                                <strong className="text-amber-300 font-serif">{seg.syllable}</strong>
                                <span className="text-neutral-500">({seg.expectedPitch})</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
